import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useSmoothScroll } from './hooks/useSmoothScroll'
import { useAuth } from './hooks/useAuth'
import ProtectedRoute from './components/Auth/ProtectedRoute'
import Navbar from './components/Layout/Navbar'
import Footer from './components/Layout/Footer'
import Loader from './components/Loader/Loader'
import DialogContainer from './components/Dialog/DialogContainer'

// Lazy load pages - solo se descargan cuando el usuario visita la ruta
// Esto reduce la carga inicial y mejora la velocidad en móvil y escritorio
const Home = lazy(() => import('./pages/Home'))
const AboutUs = lazy(() => import('./pages/AboutUs'))
const Contact = lazy(() => import('./pages/Contact'))
const Pricing = lazy(() => import('./pages/Pricing'))
const Login = lazy(() => import('./pages/Auth/Login'))
const Register = lazy(() => import('./pages/Auth/Register'))
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'))
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'))
const DashboardHome = lazy(() => import('./pages/Dashboard/DashboardHome'))
const Checklist = lazy(() => import('./pages/Dashboard/Checklist'))
const BudgetManager = lazy(() => import('./pages/Dashboard/BudgetManager'))
const GuestManager = lazy(() => import('./pages/Dashboard/GuestManager'))
const GuestManagerIndex = lazy(() => import('./pages/Dashboard/GuestManager/GuestManagerIndex'))
const TheLifesaverLink = lazy(() => import('./pages/Dashboard/GuestManager/TheLifesaverLink'))
const RSVPFormBuilder = lazy(() => import('./pages/Dashboard/GuestManager/RSVPFormBuilder'))
const CraftYourInvite = lazy(() => import('./pages/Dashboard/GuestManager/CraftYourInvite'))
const GentleReminderTime = lazy(() => import('./pages/Dashboard/GuestManager/GentleReminderTime'))
const WhoSitsWhere = lazy(() => import('./pages/Dashboard/GuestManager/WhoSitsWhere'))
const SupplierManager = lazy(() => import('./pages/Dashboard/SupplierManager'))
const SupplierManagerIndex = lazy(() => import('./pages/Dashboard/SupplierManager/SupplierManagerIndex'))
const NumbersPlease = lazy(() => import('./pages/Dashboard/SupplierManager/NumbersPlease'))
const TheChosenOnes = lazy(() => import('./pages/Dashboard/SupplierManager/TheChosenOnes'))
const TimelineCreator = lazy(() => import('./pages/Dashboard/TimelineCreator'))
const Lists = lazy(() => import('./pages/Dashboard/Lists'))
const ListsIndex = lazy(() => import('./pages/Dashboard/Lists/ListsIndex'))
const MustCaptureMoments = lazy(() => import('./pages/Dashboard/Lists/MustCaptureMoments'))
const TheSoundtrack = lazy(() => import('./pages/Dashboard/Lists/TheSoundtrack'))
const BloomBoard = lazy(() => import('./pages/Dashboard/Lists/BloomBoard'))
const WeddingCargo = lazy(() => import('./pages/Dashboard/Lists/WeddingCargo'))
const Profile = lazy(() => import('./pages/Dashboard/Profile'))
const LifesaverForm = lazy(() => import('./pages/Public/LifesaverForm'))
const RSVPInvitePage = lazy(() => import('./pages/Public/RSVPInvitePage'))
const RSVPSearch = lazy(() => import('./pages/Public/RSVPSearch'))
const RSVPForm = lazy(() => import('./pages/Public/RSVPForm'))

function App() {
  const { loading: authLoading } = useAuth()
  useSmoothScroll()

  if (authLoading) {
    return <Loader />
  }

  return (
    <div className="app">
      <DialogContainer />
      <Routes>
        <Route path="/lifesaver-link/:token" element={
          <Suspense fallback={<Loader />}>
            <LifesaverForm />
          </Suspense>
        } />
        
        <Route path="/rsvp/:slug" element={
          <Suspense fallback={<Loader />}>
            <RSVPInvitePage />
          </Suspense>
        } />
        <Route path="/rsvp/:slug/search" element={
          <Suspense fallback={<Loader />}>
            <RSVPSearch />
          </Suspense>
        } />
        <Route path="/rsvp/:slug/form/:guestId" element={
          <Suspense fallback={<Loader />}>
            <RSVPForm />
          </Suspense>
        } />
        
        <Route path="*" element={
          <>
            <Navbar />
            <main>
              <Suspense fallback={<Loader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/pricing" element={<Pricing />} />
                  
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<DashboardHome />} />
                    <Route path="checklist" element={<Checklist />} />
                    <Route path="budget-manager" element={<BudgetManager />} />
                    
                    <Route path="guest-manager" element={<GuestManager />}>
                      <Route index element={<GuestManagerIndex />} />
                      <Route path="the-lifesaver-link" element={<TheLifesaverLink />} />
                      <Route path="rsvp-form-builder" element={<RSVPFormBuilder />} />
                      <Route path="craft-your-invite" element={<CraftYourInvite />} />
                      <Route path="gentle-reminder-time" element={<GentleReminderTime />} />
                      <Route path="who-sits-where" element={<WhoSitsWhere />} />
                    </Route>
                    
                    <Route path="supplier-manager" element={<SupplierManager />}>
                      <Route index element={<SupplierManagerIndex />} />
                      <Route path="numbers-please" element={<NumbersPlease />} />
                      <Route path="the-chosen-ones" element={<TheChosenOnes />} />
                    </Route>
                    
                    <Route path="timeline-creator" element={<TimelineCreator />} />
                    
                    <Route path="lists" element={<Lists />}>
                      <Route index element={<ListsIndex />} />
                      <Route path="must-capture-moments" element={<MustCaptureMoments />} />
                      <Route path="the-soundtrack" element={<TheSoundtrack />} />
                      <Route path="bloom-board" element={<BloomBoard />} />
                      <Route path="wedding-cargo" element={<WeddingCargo />} />
                    </Route>
                  </Route>
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </>
        } />
      </Routes>
    </div>
  )
}

export default App
